import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, FileText, Shield, Send } from 'lucide-react';

const PoliciesSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    // Reset form
    setFormData({ name: '', email: '', message: '' });
  };

  const openPolicyModal = (policy) => {
    // Redirect to the appropriate policy page based on policy ID
    if (policy.id === 'privacy') {
      window.location.href = '/privacy_policy';
    } else if (policy.id === 'terms') {
      window.location.href = '/terms_policy';
    }
  };


  const policies = [
    {
      id: 'privacy',
      icon: Shield,
      title: "Privacy Policy",
      description: "How we protect and handle your personal information",
      content: `
        <h3>Privacy Policy</h3>
        <p>At MetroRahi, we are committed to protecting your privacy and ensuring the security of your personal information.</p>
        
        <h4>Information We Collect</h4>
        <ul>
          <li>Location data for route planning and navigation</li>
          <li>App usage statistics to improve our services</li>
          <li>Device information for compatibility and performance</li>
        </ul>
        
        <h4>How We Use Your Information</h4>
        <ul>
          <li>Provide accurate metro navigation services</li>
          <li>Improve app performance and user experience</li>
          <li>Send important service updates and notifications</li>
        </ul>
        
        <h4>Data Security</h4>
        <p>We implement industry-standard security measures to protect your data from unauthorized access, alteration, or disclosure.</p>
        
        <h4>Your Rights</h4>
        <p>You have the right to access, modify, or delete your personal information at any time through the app settings.</p>
      `
    },
    {
      id: 'terms',
      icon: FileText,
      title: "Terms of Service",
      description: "Our terms and conditions for using MetroRahi",
      content: `
        <h3>Terms of Service</h3>
        <p>By using MetroRahi, you agree to these terms and conditions.</p>
        
        <h4>Acceptable Use</h4>
        <ul>
          <li>Use the app for personal metro navigation only</li>
          <li>Do not attempt to reverse engineer or modify the app</li>
          <li>Respect other users and follow local laws</li>
        </ul>
        
        <h4>Service Availability</h4>
        <p>We strive to provide 24/7 service, but availability may vary due to maintenance, updates, or technical issues.</p>
        
        <h4>Limitation of Liability</h4>
        <p>MetroRahi is provided "as is" and we are not liable for any damages arising from the use of our services.</p>
        
        <h4>Updates to Terms</h4>
        <p>We may update these terms from time to time. Continued use of the app constitutes acceptance of updated terms.</p>
      `
    }
  ];

  // Handle URL hash navigation for policies
  useEffect(() => {
    const handlePolicyHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove the # symbol
      
      // Only handle policy-related hashes
      if (hash && ['privacy', 'terms'].includes(hash)) {
        // Find the policy by ID
        const policy = policies.find(p => p.id === hash);
        if (policy) {
          // Scroll to the policies section first
          const policiesSection = document.querySelector('.policies-section');
          if (policiesSection) {
            policiesSection.scrollIntoView({ behavior: 'smooth' });
          }
          
          // Open the policy modal after a short delay to allow scrolling
          setTimeout(() => {
            openPolicyModal(policy);
          }, 500);
        }
      }
    };

    // Check hash on component mount
    handlePolicyHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handlePolicyHashChange);

    return () => {
      window.removeEventListener('hashchange', handlePolicyHashChange);
    };
  }, []);

  return (
    <section id="support" className="policies-section section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Policies & Trust</h2>
          <p className="section-subtitle">
            Transparent policies and easy ways to get in touch with us
          </p>
          <motion.p
            className="company-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Brought to you by <a href="https://resiara.com" target="_blank" rel="noopener noreferrer" className="company-link"><strong>Resiara Software Pvt. Ltd.</strong></a>
          </motion.p>
        </motion.div>

        <div className="policies-content">
          {/* Policies Grid */}
          <motion.div
            className="policies-grid"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {policies.map((policy, index) => (
              <motion.div
                key={index}
                className="policy-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                onClick={() => openPolicyModal(policy)}
                style={{ cursor: 'pointer' }}
              >
                <div className="policy-icon">
                  <policy.icon />
                </div>
                <h3 className="policy-title">{policy.title}</h3>
                <p className="policy-description">{policy.description}</p>
                <div className="policy-link">
                  Read More →
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="contact-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="contact-header">
              <Mail className="contact-icon" />
              <h3>Get in Touch</h3>
              <p>Have questions or feedback? We'd love to hear from you!</p>
              <p className="contact-company">Support provided by <a href="https://resiara.com" target="_blank" rel="noopener noreferrer" className="company-link"><strong>Resiara Software Pvt. Ltd.</strong></a></p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className="form-textarea"
                ></textarea>
              </div>
              
              <motion.button
                type="submit"
                className="submit-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Send className="button-icon" />
                <span>Send Message</span>
              </motion.button>
            </form>

            {/* Alternative Contact Info */}
            <div className="contact-alternatives">
              <div className="contact-item">
                <Mail className="contact-item-icon" />
                <div>
                  <h4>Email Us</h4>
                  <p>support.metrorahi@resiara.com</p>
                </div>
              </div>
      
            </div>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default PoliciesSection;
